import { useEffect } from 'react';

import type { Camera } from '@react-three/fiber';
import { Vector3 } from 'three';

interface UseKeyboardCameraControlsProps {
  camera: Camera;
  controlsRef: React.RefObject<unknown>;
  controlsKey: number;
}

/**
 * Custom hook for keyboard camera controls
 * Handles arrow keys, WASD for panning, shift + arrow/WASD for rotation, and Q/E for yaw
 */
export function useKeyboardCameraControls({ camera, controlsRef, controlsKey }: UseKeyboardCameraControlsProps) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: need to re-run when controls remount (controlsKey changes)
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls || typeof controls !== 'object') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore keyboard controls when user is typing in an input
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      // biome-ignore lint/suspicious/noExplicitAny: OrbitControls type not exported from drei
      const ctrl = controls as any;

      const rotateSpeed = 0.1; // Radians per keypress
      const panSpeed = 0.5; // Pan distance per keypress

      // Handle Q/E for yaw rotation (no shift needed)
      if (event.key === 'q' || event.key === 'Q' || event.key === 'e' || event.key === 'E') {
        event.preventDefault();
        event.stopPropagation();

        const azimuthalAngle = ctrl.getAzimuthalAngle ? ctrl.getAzimuthalAngle() : 0;
        const polarAngle = ctrl.getPolarAngle ? ctrl.getPolarAngle() : 0;

        const newAzimuthal =
          event.key.toLowerCase() === 'q' ? azimuthalAngle + rotateSpeed : azimuthalAngle - rotateSpeed;

        // Calculate new camera position
        const target = ctrl.target;
        const distance = camera.position.distanceTo(target);

        const x = distance * Math.sin(polarAngle) * Math.sin(newAzimuthal);
        const y = distance * Math.cos(polarAngle);
        const z = distance * Math.sin(polarAngle) * Math.cos(newAzimuthal);

        camera.position.set(target.x + x, target.y + y, target.z + z);
        camera.lookAt(target);

        if ('update' in ctrl) {
          ctrl.update();
        }
        return;
      }

      // Check if this is a shift + up/down arrow or W/S combo for rotation (pitch only)
      const isRotateKey =
        event.shiftKey &&
        (event.key === 'ArrowUp' ||
          event.key === 'ArrowDown' ||
          event.key === 'w' ||
          event.key === 'W' ||
          event.key === 's' ||
          event.key === 'S');

      // Shift + A/D controls zoom
      const isZoomKey =
        event.shiftKey && (event.key === 'a' || event.key === 'A' || event.key === 'd' || event.key === 'D');

      // Shift + left/right arrows should do nothing (Q/E handle yaw without shift)
      const isIgnoredKey = event.shiftKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight');

      // Check if this is arrow/WASD without shift for panning
      const isPanKey =
        !event.shiftKey &&
        (event.key.startsWith('Arrow') ||
          event.key === 'w' ||
          event.key === 'W' ||
          event.key === 'a' ||
          event.key === 'A' ||
          event.key === 's' ||
          event.key === 'S' ||
          event.key === 'd' ||
          event.key === 'D');

      if (isIgnoredKey) {
        // Shift + left/right do nothing, but prevent default to avoid conflicts
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isZoomKey) {
        event.preventDefault();
        event.stopPropagation();

        const key = event.key.toLowerCase();
        const target = ctrl.target;
        const currentDistance = camera.position.distanceTo(target);
        const zoomSpeed = 0.5;

        // A zooms out (increase distance), D zooms in (decrease distance)
        const newDistance =
          key === 'a'
            ? Math.min(30, currentDistance + zoomSpeed) // Max distance 30
            : Math.max(5, currentDistance - zoomSpeed); // Min distance 5

        // Calculate new camera position maintaining direction
        const direction = new Vector3();
        direction.subVectors(camera.position, target).normalize();
        camera.position.copy(target).add(direction.multiplyScalar(newDistance));

        if ('update' in ctrl) {
          ctrl.update();
        }
        return;
      }

      if (isRotateKey) {
        event.preventDefault();
        event.stopPropagation();

        // Get current angles
        const azimuthalAngle = ctrl.getAzimuthalAngle ? ctrl.getAzimuthalAngle() : 0;
        const polarAngle = ctrl.getPolarAngle ? ctrl.getPolarAngle() : 0;

        let newPolar = polarAngle;

        const key = event.key.toLowerCase();
        switch (key) {
          case 'arrowup':
          case 'w':
            newPolar = Math.max(0.1, newPolar - rotateSpeed);
            break;
          case 'arrowdown':
          case 's':
            newPolar = Math.min(Math.PI / 2 - 0.1, newPolar + rotateSpeed);
            break;
        }

        // Calculate new camera position using spherical coordinates (pitch only)
        const target = ctrl.target;
        const distance = camera.position.distanceTo(target);

        const x = distance * Math.sin(newPolar) * Math.sin(azimuthalAngle);
        const y = distance * Math.cos(newPolar);
        const z = distance * Math.sin(newPolar) * Math.cos(azimuthalAngle);

        camera.position.set(target.x + x, target.y + y, target.z + z);
        camera.lookAt(target);

        if ('update' in ctrl) {
          ctrl.update();
        }
      } else if (isPanKey) {
        event.preventDefault();
        event.stopPropagation();

        // Manually pan the camera by moving both camera and target
        const key = event.key.toLowerCase();

        // Get camera's right and up vectors in world space
        const right = new Vector3();
        const up = new Vector3();

        camera.matrix.extractBasis(right, up, new Vector3());
        right.normalize();
        up.normalize();

        const offset = new Vector3();

        switch (key) {
          case 'arrowleft':
          case 'a':
            offset.addScaledVector(right, -panSpeed);
            break;
          case 'arrowright':
          case 'd':
            offset.addScaledVector(right, panSpeed);
            break;
          case 'arrowup':
          case 'w':
            offset.addScaledVector(up, panSpeed);
            break;
          case 'arrowdown':
          case 's':
            offset.addScaledVector(up, -panSpeed);
            break;
        }

        // Move both camera and target
        camera.position.add(offset);
        if (ctrl.target) {
          ctrl.target.add(offset);
        }

        if ('update' in ctrl) {
          ctrl.update();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [camera, controlsKey, controlsRef]);
}
